import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Grid, IconButton,
  Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SpaIcon from '@mui/icons-material/Spa';
import RefreshIcon from '@mui/icons-material/Refresh';
import TodayIcon from '@mui/icons-material/Today';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SidebarLayout from '../components/sidebarLayout';
import { usePermissions } from '../contexts/PermissionsContext';
import { getSpaDashboard, getSpaPlanner, getSpaCabinBoard, getSpaWaitlistOffers } from '../APIs/SpaAPI';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

export const dashboardDateKey = (now = new Date(), timeZone = 'Europe/Rome') => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const value = (type) => parts.find((part) => part.type === type).value;
  return `${value('year')}-${value('month')}-${value('day')}`;
};

export function dashboardAppointmentStatus(appointment) {
  if (['CANCELLED', 'NO_SHOW'].includes(appointment.commercialStatus)) return appointment.commercialStatus;
  return appointment.operationalStatus !== 'SCHEDULED'
    ? appointment.operationalStatus : appointment.commercialStatus;
}

export function dashboardCabinCurrentStatus(cabin, now = Date.now()) {
  if (cabin.active === false) return 'OUT_OF_SERVICE';
  const instant = new Date(now).getTime();
  const interval = (cabin.statusIntervals || []).find((item) =>
    new Date(item.startAt).getTime() <= instant && instant < new Date(item.endAt).getTime());
  if (['MAINTENANCE', 'OUT_OF_SERVICE'].includes(interval?.status)) return interval.status;
  const inProgress = (cabin.appointments || []).some((item) =>
    item.operationalStatus === 'IN_PROGRESS' && !['CANCELLED', 'NO_SHOW'].includes(item.commercialStatus));
  if (inProgress) return 'OCCUPIED';
  // Scheduled occupancy reserves a resource; it does not prove a client is still inside.
  return interval?.status === 'OCCUPIED' ? 'RESERVED' : interval?.status || 'AVAILABLE';
}

const personName = (person) => [person?.name, person?.surname].filter(Boolean).join(' ') || '-';
const categories = ['dailyAppointments', 'presentClients', 'nextAppointments', 'arrivals', 'waitingClients',
  'inProgress', 'completed', 'cancellations', 'noShows'];
const metrics = [
  { key: 'arrivals', tone: '#6B4CA5' },
  { key: 'waiting', tone: '#D47200' },
  { key: 'inProgress', tone: '#9A5B00' },
  { key: 'completed', tone: '#166534' },
  { key: 'noShows', tone: '#991B1B' },
];
const additionalMetricKeys = ['present', 'nextAppointments', 'cancellations', 'activeWaitlist'];
const statusColors = {
  ARRIVED: 'info', WAITING: 'warning', IN_PROGRESS: 'warning', COMPLETED: 'success',
  CANCELLED: 'error', NO_SHOW: 'error', CONFIRMED: 'success', GUARANTEED: 'success',
};
const alertColors = { INFO: 'info', WARNING: 'warning', CRITICAL: 'error' };
const cabinStatusColors = { AVAILABLE: 'success', OCCUPIED: 'warning', RESERVED: 'info', PREPARATION: 'info', CLEANING: 'warning', MAINTENANCE: 'error', OUT_OF_SERVICE: 'error' };
const sectionGridSx = { width: 'calc(100% + 16px)', ml: '-16px !important' };

function Section({ title, icon, count, action, children }) {
  return <Paper component="section" sx={{ p: 2.25, height: '100%', borderRadius: 2, minWidth: 0 }}>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.75 }}>
      {icon}<Typography component="h2" variant="h6" fontWeight={850}>{title}</Typography>
      {count !== undefined && <Chip size="small" label={count} />}
      {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
    </Stack>
    {children}
  </Paper>;
}

function Rows({ items, empty, children }) {
  if (!items.length) return <Typography color="text.secondary" sx={{ py: 1 }}>{empty}</Typography>;
  return <Stack divider={<Divider />} spacing={1.5}>{items.map(children)}</Stack>;
}

function SpaDashboardPage() {
  const { t, i18n } = useTranslation();
  const d = (key, options) => t(`spa.daily.${key}`, options);
  const theme = useTheme();
  const navigate = useNavigate();
  const { can, loading: permissionsLoading } = usePermissions();
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const tenantType = String(localStorage.getItem('tenantType') || '').toUpperCase();
  const canView = !permissionsLoading && can('spa-reports:view');
  const canViewAppointments = canView && can('spa-appointments:view');
  const canViewResources = canViewAppointments && can('spa-resources:view');
  const canViewClients = canView && can('spa-clients:view');
  const [date, setDate] = useState(() => dashboardDateKey());
  const [reload, setReload] = useState(0);
  const [category, setCategory] = useState('dailyAppointments');
  const [state, setState] = useState(null);
  const [clock, setClock] = useState(Date.now());
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    && !Number.isNaN(Date.parse(`${date}T00:00:00Z`))
    && new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) === date;
  const key = `${tenant}:${token}:${date}:${canView}:${canViewAppointments}:${canViewResources}:${canViewClients}`;
  const current = state?.key === key ? state : null;
  const data = canView && current?.data;
  const timeZone = data?.timeZone || 'Europe/Rome';
  const selectedDayIsToday = date === dashboardDateKey(new Date(clock), timeZone);
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const time = (value) => value ? new Intl.DateTimeFormat(locale, {
    timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(value)) : '-';
  const dateTime = (value) => value ? new Intl.DateTimeFormat(locale, {
    timeZone, dateStyle: 'short', timeStyle: 'short',
  }).format(new Date(value)) : '-';
  const range = (start, end) => `${time(start)} - ${time(end)}`;
  const status = (value, cabin = false) => <Chip size="small" color={(cabin ? cabinStatusColors : statusColors)[value] || 'default'}
    label={t(`spa.daily.${cabin ? 'cabinStatuses' : 'statuses'}.${value}`, { defaultValue: value })} />;

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!canView || tenantType !== 'SPA' || !token || !tenant || !validDate) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    setState({ key, loading: true });
    const update = (values) => {
      if (!signal.aborted) setState((previous) => previous?.key === key ? { ...previous, ...values } : previous);
    };
    getSpaDashboard(date, token, tenant, { signal }).then((result) => {
      if (signal.aborted) return;
      update({ data: result, loading: false, boardLoading: canViewResources,
        offersLoading: canViewAppointments, appointmentsLoading: canViewAppointments });
      // The consolidated response does not include past-start, still-scheduled appointments.
      if (canViewAppointments) {
        getSpaPlanner({ date, view: 'day' }, token, tenant, { onlineOnly: true, signal }).then((planner) => {
          update({ appointments: planner.appointments, appointmentsLoading: false });
        }).catch((error) => update({
          appointmentsError: localizedErrorMessage(error),
          appointmentsLoading: false,
        }));
      }
      if (canViewResources) {
        getSpaCabinBoard({ from: result.from, to: result.to }, token, tenant, { onlineOnly: true, signal })
          .then((board) => {
            if (!Array.isArray(board?.cabins)) throw new Error('Invalid cabin board response');
            update({ cabins: board.cabins, boardLoading: false });
          }).catch((error) => update({
            boardError: localizedErrorMessage(error),
            boardLoading: false,
          }));
      }
      if (canViewAppointments) {
        getSpaWaitlistOffers({ status: 'ACTIVE' }, token, tenant, { signal }).then((offers) => {
          if (!Array.isArray(offers)) throw new Error('Invalid offers response');
          update({ offers, offersLoading: false });
        }).catch((error) => update({
          offersError: localizedErrorMessage(error),
          offersLoading: false,
        }));
      }
    }).catch((error) => update({
      error: localizedErrorMessage(error),
      status: error.status,
      loading: false,
    }));
    return () => controller.abort();
  }, [date, reload, canView, canViewAppointments, canViewResources, key, token, tenant, tenantType, validDate]);

  const refresh = () => { setClock(Date.now()); setReload((value) => value + 1); };
  const liveOffers = (current?.offers || []).filter((offer) => offer.status === 'ACTIVE'
    && new Date(offer.expiresAt).getTime() > clock
    && new Date(offer.startAt) < new Date(data?.to)
    && new Date(offer.endAt) > new Date(data?.from));
  const profileLink = (client) => canViewClients && client?.id
    ? <Button size="small" onClick={() => navigate(`/spa/clients/${client.id}`)}>{d('profile')}</Button> : null;
  const waitlistLink = canViewAppointments
    ? <Button size="small" onClick={() => navigate('/spa/waitlist')}>{d('waitlistAction')}</Button> : null;
  const spinner = (label) => <CircularProgress size={24} aria-label={label} />;
  const selectedCategory = !canViewAppointments && category === 'dailyAppointments' ? 'presentClients' : category;
  const selectedAppointments = selectedCategory === 'dailyAppointments' ? current?.appointments || [] : data?.[selectedCategory] || [];

  let message;
  if (tenantType !== 'SPA') message = t('spa.common.spaOnly');
  else if (!token || !tenant) message = d('signIn');
  else if (!permissionsLoading && !canView) message = d('denied');

  const timeline = <Section title={d(date === dashboardDateKey(new Date(), timeZone) ? 'timeline' : 'datedTimeline')} icon={<EventAvailableIcon color="primary" />}
    count={selectedCategory === 'dailyAppointments' && (current?.appointmentsLoading || current?.appointmentsError) ? undefined : selectedAppointments.length}
    action={<TextField select size="small" label={d('category')} value={selectedCategory} SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }} onChange={(event) => setCategory(event.target.value)} sx={{ width: 230, maxWidth: '100%' }}>
      {categories.filter((name) => name !== 'dailyAppointments' || canViewAppointments)
        .map((name) => <option key={name} value={name}>{d(name)}</option>)}
    </TextField>}>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.75 }}>
      {additionalMetricKeys.map((name) => <Chip key={name} size="small" variant="outlined"
        label={<span>{d(name)}: <span data-testid={'metric-' + name}>{data?.summary?.[name]}</span></span>} />)}
    </Stack>
    <Box role="region" aria-label={d('appointments')}>
      {selectedCategory === 'dailyAppointments' && current?.appointmentsLoading ? spinner(d('appointments'))
        : selectedCategory === 'dailyAppointments' && current?.appointmentsError ? <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{d('retry')}</Button>}>{d('appointmentsError')} {current.appointmentsError}</Alert>
          : !selectedAppointments.length ? <Typography color="text.secondary" sx={{ py: 2 }}>{d('emptyAppointments')}</Typography>
            : <Stack spacing={1}>{selectedAppointments.map((item) => <Paper key={item.id} variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, borderLeft: '5px solid', borderLeftColor: theme.palette[statusColors[dashboardAppointmentStatus(item)] || 'grey']?.main || '#455A64' }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={1.4}><Typography fontWeight={850}>{time(item.scheduledStartAt)}</Typography>
                  <Typography variant="caption" color="text.secondary">{time(item.scheduledEndAt)}</Typography></Grid>
                <Grid item xs={12} sm={4}><Typography fontWeight={850}>{personName(item.customer)}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.treatment?.name || '-'}</Typography>
                  {profileLink(item.customer)}</Grid>
                <Grid item xs={12} sm={2.6}><Typography variant="body2">{item.resources?.operators?.map(personName).join(', ') || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">{d('operator')}</Typography></Grid>
                <Grid item xs={12} sm={2}><Typography variant="body2">{item.resources?.cabins?.map((cabin) => cabin.name).join(', ') || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">{d('cabin')}</Typography></Grid>
                <Grid item xs={12} sm={2}>{status(dashboardAppointmentStatus(item))}</Grid>
              </Grid>
            </Paper>)}</Stack>}
    </Box>
  </Section>;

  const cabinBoard = <Section title={d('cabinBoard')} icon={<MeetingRoomIcon color="error" />}
    count={current?.boardLoading || current?.boardError || !canViewResources ? undefined : current?.cabins?.length}>
    {!canViewResources ? <Alert severity="info">{d('resourcesDenied')}</Alert> : current?.boardLoading ? spinner(d('cabinBoard'))
      : current?.boardError ? <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{d('retry')}</Button>}>{d('boardError')} {current.boardError}</Alert>
        : !(current?.cabins || []).length ? <Typography color="text.secondary" sx={{ py: 2 }}>{d('emptyBoard')}</Typography>
          : <Stack spacing={1}>{current.cabins.map((cabin) => {
            const intervals = (cabin.statusIntervals || []).filter((interval) => interval.status !== 'AVAILABLE');
            const currentStatus = dashboardCabinCurrentStatus(cabin, clock);
            return <Paper key={cabin.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography fontWeight={850}>{cabin.name}</Typography>
                {selectedDayIsToday && <Chip size="small" color={cabinStatusColors[currentStatus] || 'default'}
                  label={d('nowStatus', { status: t(`spa.daily.cabinStatuses.${currentStatus}`, { defaultValue: currentStatus }) })} />}
              </Stack>
              <Stack spacing={1} sx={{ mt: intervals.length ? 1 : 0 }}>
                {intervals.length > 0 && <Typography variant="caption" color="text.secondary">{d('daySchedule')}</Typography>}
                {intervals.map((interval, index) => <Stack key={index} direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                  {status(interval.status === 'OCCUPIED' ? 'RESERVED' : interval.status, true)}<Typography variant="caption" color="text.secondary">{range(interval.startAt, interval.endAt)}</Typography>
                </Stack>)}
                {!intervals.length && !selectedDayIsToday && <Typography variant="body2" color="text.secondary">{d('noScheduledUse')}</Typography>}
              </Stack>
            </Paper>;
          })}</Stack>}
  </Section>;

  return <SidebarLayout><Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320, mx: 'auto', minWidth: 0, overflowWrap: 'anywhere' }}>
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Typography component="h1" variant="h4" fontWeight={850}>{d('title')}</Typography>
          <Typography color="text.secondary">{d('subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField type="date" size="small" label={d('date')} value={date} InputLabelProps={{ shrink: true }}
            onChange={(event) => setDate(event.target.value)} error={!validDate} sx={{ width: 170 }} />
          <Tooltip title={d('today')}><IconButton aria-label={d('today')} onClick={() => setDate(dashboardDateKey(new Date(), timeZone))}><TodayIcon /></IconButton></Tooltip>
          <Chip icon={<SpaIcon />} color="primary" label={t('spa.common.liveData', { defaultValue: 'Live data' })} />
          <Tooltip title={d('refresh')}><span><IconButton aria-label={d('refresh')} onClick={refresh}
            disabled={!canView || !validDate || current?.loading}><RefreshIcon /></IconButton></span></Tooltip>
          {canViewAppointments && <Button variant="contained" onClick={() => navigate('/spa/operations')}>{d('operations')}</Button>}
        </Stack>
      </Stack>
      {message ? <Alert severity="warning">{message}</Alert> : !validDate ? <Alert severity="warning">{d('invalidDate')}</Alert>
        : permissionsLoading || !current || current.loading ? spinner(d('loading'))
          : current.error ? <Stack spacing={2}><Alert severity={current.status === 403 ? 'warning' : 'error'}>
            {current.status === 403 ? d('denied') : d('loadError') + ' ' + current.error}</Alert>
            <Box><Button startIcon={<RefreshIcon />} onClick={refresh}>{d('retry')}</Button></Box></Stack>
            : data && <>
              <Grid container spacing={2} data-testid="dashboard-metrics" sx={sectionGridSx}>
                {metrics.map(({ key: name, tone }) => <Grid item xs={12} sm={6} md={2.4} key={name}>
                  <Paper sx={{ p: 2, minHeight: 112, borderRadius: 2, borderTop: '4px solid ' + tone,
                    background: theme.palette.mode === 'dark' ? alpha(tone, 0.12)
                      : 'linear-gradient(180deg, ' + alpha(tone, 0.08) + ' 0%, #fff 100%)' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={700}>{d('metricLabels.' + name)}</Typography>
                    <Typography component="p" variant="h4" fontWeight={900} data-testid={'metric-' + name}>{data.summary[name]}</Typography>
                  </Paper>
                </Grid>)}
              </Grid>
              <Grid container spacing={2} data-testid="dashboard-primary" sx={sectionGridSx}>
                <Grid item xs={12} lg={8} data-testid="dashboard-timeline">{timeline}</Grid>
                <Grid item xs={12} lg={4} data-testid="dashboard-cabin-board">{cabinBoard}</Grid>
              </Grid>
              <Grid container spacing={2} data-testid="dashboard-secondary" sx={sectionGridSx}>
                <Grid item xs={12} md={6}>
                  <Section title={d('unavailableCabins')} icon={<MeetingRoomIcon color="error" />} count={data.unavailableCabins.length}>
                    <Rows items={data.unavailableCabins} empty={d('emptyCabins')}>{(item) => <Box key={item.id}>
                      <Stack direction="row" spacing={1} alignItems="center"><Typography fontWeight={850}>{item.name}</Typography>{!item.active && <Chip size="small" color="error" label={d('inactive')} />}</Stack>
                      {item.blocks.map((block) => <Box key={block.id} sx={{ mt: 1 }}>{status(block.type, true)}
                        <Typography variant="body2">{dateTime(block.startAt)} - {dateTime(block.endAt)}</Typography>
                        {block.note && <Typography variant="body2">{block.note}</Typography>}</Box>)}
                    </Box>}</Rows>
                  </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Section title={d('notes')} icon={<WarningAmberIcon color="warning" />} count={data.notes.length}>
                    <Rows items={data.notes} empty={d('emptyNotes')}>{(note) => <Box key={note.id}>
                      <Typography sx={{ whiteSpace: 'pre-line' }}>{note.note}</Typography>
                      {note.author && <Typography variant="caption" color="text.secondary">{personName(note.author)}</Typography>}
                    </Box>}</Rows>
                  </Section>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={sectionGridSx}>
                <Grid item xs={12} md={6}>
                  <Section title={d('absentOperators')} icon={<PersonOffIcon color="warning" />} count={data.absentOperators.length}>
                    <Rows items={data.absentOperators} empty={d('emptyOperators')}>{(item) => <Box key={item.scheduleEntryId}>
                      <Typography fontWeight={850}>{personName(item.operator)}</Typography>
                      <Typography variant="body2">{dateTime(item.startAt)} - {dateTime(item.endAt)}</Typography>
                      {item.note && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{item.note}</Typography>}
                    </Box>}</Rows>
                  </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Section title={d('vipAndAlerts')} icon={<WarningAmberIcon color="warning" />} count={data.vipAndAlerts.length}>
                    <Rows items={data.vipAndAlerts} empty={d('emptyAlerts')}>{(item) => <Box key={item.client.id}>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap"><Typography fontWeight={850}>{personName(item.client)}</Typography>{item.isVip && <Chip size="small" label={d('vip')} color="warning" />}{profileLink(item.client)}</Stack>
                      <Stack spacing={1} sx={{ mt: 1 }}>{item.alerts.map((alert) => <Alert key={alert.id} severity={alertColors[alert.severity] || 'info'}>
                        <Typography variant="body2" fontWeight={700}>{t('spa.daily.alertTypes.' + alert.type, { defaultValue: alert.type })}</Typography>{alert.message}
                      </Alert>)}</Stack>
                    </Box>}</Rows>
                  </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Section title={d('waitlist')} icon={<ListAltIcon />} count={data.waitlist.length} action={waitlistLink}>
                    <Rows items={data.waitlist} empty={d('emptyWaitlist')}>{(item) => <Box key={item.id}>
                      <Typography fontWeight={850}>{personName(item.client)} / {item.treatment.name}</Typography>
                      <Typography variant="body2">{d('party', { count: item.partySize })} / {item.preferredOperator ? personName(item.preferredOperator) : d('anyOperator')}</Typography>
                      <Typography variant="body2" color="text.secondary">{dateTime(item.desiredStartAt)} - {dateTime(item.desiredEndAt)}</Typography>
                    </Box>}</Rows>
                  </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Section title={d('offers')} icon={<EventAvailableIcon color="warning" />} count={!canViewAppointments || current.offersLoading || current.offersError ? undefined : liveOffers.length} action={waitlistLink}>
                    {!canViewAppointments ? <Alert severity="info">{d('offersDenied')}</Alert>
                      : current.offersLoading ? spinner(d('offers')) : current.offersError ? <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{d('retry')}</Button>}>{d('offersError')} {current.offersError}</Alert>
                      : <Rows items={liveOffers} empty={d('emptyOffers')}>{(offer) => <Box key={offer.id}>
                        <Typography fontWeight={850}>{personName(offer.request.client)} / {offer.request.treatment.name}</Typography>
                        <Typography variant="body2">{range(offer.startAt, offer.endAt)}</Typography>
                        <Typography variant="caption" color="text.secondary">{d('expires', { time: dateTime(offer.expiresAt) })}</Typography>
                      </Box>}</Rows>}
                  </Section>
                </Grid>
              </Grid>
              {canViewAppointments && <Box><Button startIcon={<EventAvailableIcon />} onClick={() => navigate('/spa/planner?date=' + date)}>{d('planner')}</Button></Box>}
            </>}
    </Stack>
  </Box></SidebarLayout>;
}

export default SpaDashboardPage;
